import { type FC, useMemo, useState } from "react";
import { IonContent, IonModal } from "@ionic/react";
import { FormattedMessage } from "react-intl";
import { useHistory } from "react-router-dom";
import {
  IconInfoCircle,
  IconSparkles,
  IconBook2,
  IconUsers,
  IconShoppingBag,
  IconChevronRight,
  IconX,
} from "@tabler/icons-react";

import styles from "./NavBarWhatsNewItem.module.css";

type WhatsNewItem = {
  key: string;
  titleId: string;
  descriptionId: string;
  path?: string;
  links?: Array<{ labelId: string; href: string }>;
  icon: "itemsCatalog" | "followups" | "mobileTeam";
};

const WhatsNewItemIcon: FC<{ icon: WhatsNewItem["icon"] }> = ({ icon }) => {
  switch (icon) {
    case "itemsCatalog":
      return <IconShoppingBag size={18} />;
    case "followups":
      return <IconBook2 size={18} />;
    case "mobileTeam":
      return <IconUsers size={18} />;
    default:
      return <IconInfoCircle size={18} />;
  }
};

const WHATS_NEW_VERSION = "2026-04-20-items-catalog";
const WHATS_NEW_STORAGE_KEY = "visito.whatsNew.lastSeenVersion";
const WHATS_NEW_HIGHLIGHT_KEYS = new Set(["itemsCatalog"]);
const WHATS_NEW_HIGHLIGHT_COUNT = WHATS_NEW_HIGHLIGHT_KEYS.size;

const getHasUnseenWhatsNew = () => {
  try {
    return localStorage.getItem(WHATS_NEW_STORAGE_KEY) !== WHATS_NEW_VERSION;
  } catch {
    return true;
  }
};

const NavBarWhatsNewItem: FC = () => {
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnseen, setHasUnseen] = useState<boolean>(getHasUnseenWhatsNew);
  const [showNewHighlight, setShowNewHighlight] = useState(false);

  const items = useMemo<WhatsNewItem[]>(
    () => [
      {
        key: "itemsCatalog",
        icon: "itemsCatalog",
        titleId: "nav.whatsNew.item.itemsCatalog.title",
        descriptionId: "nav.whatsNew.item.itemsCatalog.desc",
        path: "/payments",
      },
      {
        key: "followups",
        icon: "followups",
        titleId: "nav.whatsNew.item.followUps.title",
        descriptionId: "nav.whatsNew.item.followUps.desc",
        path: "/ai/follow-ups",
      },
      {
        key: "mobileTeam",
        icon: "mobileTeam",
        titleId: "nav.whatsNew.item.mobileTeam.title",
        descriptionId: "nav.whatsNew.item.mobileTeam.desc",
        links: [
          {
            labelId: "nav.whatsNew.item.mobileTeam.android",
            href: "https://play.google.com/store/apps/details?id=visito.app.production",
          },
          {
            labelId: "nav.whatsNew.item.mobileTeam.ios",
            href: "https://apps.apple.com/app/id6475635950",
          },
        ],
      },
    ],
    []
  );

  const openWhatsNew = () => {
    const shouldHighlight = hasUnseen;
    setShowNewHighlight(shouldHighlight);
    setIsOpen(true);

    if (!shouldHighlight) return;
    try {
      localStorage.setItem(WHATS_NEW_STORAGE_KEY, WHATS_NEW_VERSION);
    } catch {}
    setHasUnseen(false);
  };

  const onOpenItem = (path: string) => {
    setIsOpen(false);
    history.push(path);
  };

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={styles.trigger}
        onClick={openWhatsNew}
      >
        <IconSparkles size={18} />
        <span>
          <FormattedMessage id="nav.whatsNew.trigger" />
        </span>
        {hasUnseen ? (
          <span className={styles.triggerBadge}>{WHATS_NEW_HIGHLIGHT_COUNT}</span>
        ) : null}
      </button>

      <IonModal isOpen={isOpen} onWillDismiss={() => setIsOpen(false)} className={styles.modal}>
        <IonContent className={styles.content}>
          <div className={styles.card}>
            <div className={styles.header}>
              <div>
                <h3 className={styles.title}>
                  <FormattedMessage id="nav.whatsNew.title" />
                </h3>
                <p className={styles.subtitle}>
                  <FormattedMessage id="nav.whatsNew.subtitle" />
                </p>
              </div>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={() => setIsOpen(false)}
              >
                <IconX size={18} />
              </button>
            </div>

            <div className={styles.list}>
              {items.map((item) =>
                item.path ? (
                  <button
                    key={item.key}
                    type="button"
                    className={`${styles.row} ${styles.rowClickable} ${
                      showNewHighlight && WHATS_NEW_HIGHLIGHT_KEYS.has(item.key)
                        ? styles.rowNew
                        : ""
                    }`}
                    onClick={() => onOpenItem(item.path || "/")}
                  >
                    <div className={styles.rowMain}>
                      <span className={styles.rowIcon}>
                        <WhatsNewItemIcon icon={item.icon} />
                      </span>
                      <div className={styles.rowText}>
                        <div className={styles.rowTitle}>
                          <FormattedMessage id={item.titleId} />
                          {showNewHighlight &&
                          WHATS_NEW_HIGHLIGHT_KEYS.has(item.key) ? (
                            <span className={styles.newPill}>
                              <FormattedMessage id="nav.whatsNew.new" />
                            </span>
                          ) : null}
                        </div>
                        <div className={styles.rowDesc}>
                          <FormattedMessage id={item.descriptionId} />
                        </div>
                      </div>
                    </div>
                    <span className={styles.rowChevron}>
                      <IconChevronRight size={16} />
                    </span>
                  </button>
                ) : (
                  <div
                    key={item.key}
                    className={`${styles.row} ${
                      showNewHighlight && WHATS_NEW_HIGHLIGHT_KEYS.has(item.key)
                        ? styles.rowNew
                        : ""
                    }`}
                  >
                    <div className={styles.rowMain}>
                      <span className={styles.rowIcon}>
                        <WhatsNewItemIcon icon={item.icon} />
                      </span>
                      <div className={styles.rowText}>
                        <div className={styles.rowTitle}>
                          <FormattedMessage id={item.titleId} />
                          {showNewHighlight &&
                          WHATS_NEW_HIGHLIGHT_KEYS.has(item.key) ? (
                            <span className={styles.newPill}>
                              <FormattedMessage id="nav.whatsNew.new" />
                            </span>
                          ) : null}
                        </div>
                        <div className={styles.rowDesc}>
                          <FormattedMessage id={item.descriptionId} />
                        </div>
                        {item.links?.length ? (
                          <div className={styles.rowLinks}>
                            {item.links.map((link) => (
                              <a
                                key={link.labelId}
                                href={link.href}
                                target="_blank"
                                rel="noreferrer"
                                className={styles.rowLink}
                              >
                                <FormattedMessage id={link.labelId} />
                              </a>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </IonContent>
      </IonModal>
    </div>
  );
};

export default NavBarWhatsNewItem;
